import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { downloadErrorMessage } from "../lib/fileOpen";
import { compressFileList, formatBytes } from "../lib/imageCompression";
import { Button } from "./ui/button";
import { toast } from "sonner";
import offlinePhotoQueue from "../lib/offlinePhotoQueue";
import {
  Folder, Upload, File as FileIcon, DownloadSimple, Trash, Share, ArrowSquareOut,
  Image as ImageIcon, VideoCamera, FileText
} from "@phosphor-icons/react";

const _ADMIN_ROLES = ["super_admin", "admin", "project_manager"];

function prettySize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fileIconFor(mime) {
  if (!mime) return FileIcon;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("video/")) return VideoCamera;
  if (mime === "application/pdf" || mime.startsWith("text/")) return FileText;
  return FileIcon;
}

/**
 * Drop-in Files tab for any entity detail page.
 * - Admin: lazily creates the entity folder + renders full browser (subfolders + files).
 * - Vendor: only sees files in folders linked to jobs they're assigned to.
 */
export default function EntityFiles({ entityType, entityId, currentUser, className = "" }) {
  const isAdmin = _ADMIN_ROLES.includes(currentUser?.role);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async (targetFolderId = null) => {
    setLoading(true);
    try {
      if (targetFolderId) {
        const { data: d } = await API.get(`/folders/${targetFolderId}`);
        setData({
          folder: d.folder,
          children: d.children,
          files: d.files,
          breadcrumb: d.breadcrumb,
          rootFolderId: data?.rootFolderId,
        });
      } else {
        const { data: d } = await API.get(`/entity-folders/${entityType}/${entityId}`);
        setData({
          folder: d.folder,
          children: d.children,
          files: d.files,
          breadcrumb: [{ id: d.folder.id, name: d.folder.name }],
          rootFolderId: d.folder.id,
        });
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 404 && !isAdmin) {
        setData({ empty: true });
      } else if (status === 403) {
        setData({ denied: true });
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load files");
        setData({ error: true });
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const goFolder = (id) => { setActiveFolderId(id); load(id); };

  const goRoot = () => { setActiveFolderId(null); load(); };

  const handleUpload = async (files) => {
    if (!files?.length || !data?.folder) return;
    const targetFolderId = data.folder.id;
    const { files: prepared, saved } = await compressFileList(files);

    // Offline? Save blobs locally — they upload automatically on reconnect.
    if (!navigator.onLine) {
      const queued = await offlinePhotoQueue.queuePhotos(prepared, {
        folderId: targetFolderId, entityType, entityId,
      }).catch(() => 0);
      if (queued) {
        toast.info(`${queued} file${queued > 1 ? "s" : ""} saved offline — they'll upload automatically when you're back online.`);
      } else {
        toast.error("Could not save files offline on this device");
      }
      return;
    }

    if (saved > 200 * 1024) toast.info(`Compressed — saved ${formatBytes(saved)}`, { duration: 2500 });
    const toastId = toast.loading(`Uploading ${prepared.length} file${prepared.length > 1 ? "s" : ""}…`);
    let ok = 0;
    let queuedOffline = 0;
    for (const f of prepared) {
      const fd = new FormData();
      fd.append("folder_id", targetFolderId);
      fd.append("file", f);
      try {
        await API.post("/files/upload", fd);
        ok += 1;
      } catch (err) {
        if (!err.response) {
          // Network dropped mid-upload — queue the remainder offline
          const q = await offlinePhotoQueue.queuePhotos([f], {
            folderId: targetFolderId, entityType, entityId,
          }).catch(() => 0);
          queuedOffline += q;
        } else {
          const msg = formatApiError(err.response?.data?.detail) || "failed";
          toast.error(`${f.name}: ${msg}`);
        }
      }
    }
    toast.dismiss(toastId);
    if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
    if (queuedOffline) toast.info(`${queuedOffline} file${queuedOffline > 1 ? "s" : ""} queued — will upload when connection returns.`);
    load(activeFolderId);
  };

  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Delete ${type} "${name}"?`)) return;
    try {
      await API.delete(type === "folder" ? `/folders/${id}` : `/files/${id}`);
      toast.success(`${type === "folder" ? "Folder" : "File"} deleted`);
      load(activeFolderId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const handleDownload = async (file) => {
    try {
      const res = await API.get(`/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_name || file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(await downloadErrorMessage(e));
    }
  };

  const handleShare = async (kind, id, name) => {
    const password = window.prompt(`Set a password for the share link for "${name}" (leave blank for none):`, "");
    if (password === null) return;
    const expiresChoice = window.prompt("Expires in N days? Leave blank for never.", "7");
    if (expiresChoice === null) return;
    try {
      const body = {
        [kind === "folder" ? "folder_id" : "file_id"]: id,
        password: password.trim() || null,
        allow_download: true,
      };
      if (expiresChoice && !isNaN(parseInt(expiresChoice, 10))) {
        const d = new Date();
        d.setDate(d.getDate() + parseInt(expiresChoice, 10));
        body.expires_at = d.toISOString();
      }
      const { data: share } = await API.post("/shares", body);
      const url = `${window.location.origin}/share/${share.token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link created & copied to clipboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create share");
    }
  };

  // ---------- Render ----------
  if (loading) {
    return <div className={`text-sm text-slate-400 p-6 ${className}`}>Loading files…</div>;
  }

  if (data?.denied) {
    return <div className={`text-sm text-amber-600 p-4 ${className}`}>You don't have permission to view files for this item.</div>;
  }
  if (data?.empty) {
    return <div className={`text-sm text-slate-500 p-4 ${className}`}>No files have been uploaded for this item yet.</div>;
  }
  if (!data?.folder) {
    return <div className={`text-sm text-red-500 p-4 ${className}`}>Files unavailable.</div>;
  }

  const atRoot = !activeFolderId || activeFolderId === data.rootFolderId;

  return (
    <div className={`space-y-4 ${className}`} data-testid="entity-files">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Folder className="h-4 w-4 text-blue-500" />
          {!atRoot && (
            <button onClick={goRoot} className="text-blue-600 hover:underline" data-testid="entity-files-root-btn">
              {data.breadcrumb?.[0]?.name || "Root"}
            </button>
          )}
          {!atRoot && <span className="text-slate-400">/</span>}
          <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
            {data.folder.name}
          </span>
          <Link to={`/files/${data.folder.id}`} className="ml-2 text-slate-400 hover:text-slate-600" title="Open in File Hub">
            <ArrowSquareOut className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" multiple hidden
                 onChange={(e) => handleUpload(Array.from(e.target.files || []))}
                 data-testid="entity-files-upload-input" />
          <Button size="sm" onClick={() => fileInputRef.current?.click()} data-testid="entity-files-upload-btn">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload
          </Button>
        </div>
      </div>

      {/* Subfolders */}
      {!!data.children?.length && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {data.children.map((f) => (
            <button key={f.id} onClick={() => goFolder(f.id)}
                    className="text-left p-2.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-400 transition flex items-center gap-2"
                    data-testid={`entity-files-subfolder-${f.id}`}>
              <Folder className="h-5 w-5 text-blue-500 shrink-0" />
              <span className="text-sm font-medium truncate">{f.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Files */}
      {!!data.files?.length ? (
        <div className="space-y-1">
          {data.files.map((f) => {
            const Icon = fileIconFor(f.mime_type);
            return (
              <div key={f.id}
                   className="flex items-center gap-3 p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                   data-testid={`entity-files-file-${f.id}`}>
                <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{f.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {prettySize(f.size)} · {f.uploaded_by_name || "Admin"} · {new Date(f.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleDownload(f)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600" title="Download" data-testid={`entity-files-download-${f.id}`}>
                    <DownloadSimple className="h-4 w-4" />
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => handleShare("file", f.id, f.name)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600" title="Share" data-testid={`entity-files-share-${f.id}`}>
                        <Share className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete("file", f.id, f.name)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-red-600" title="Delete" data-testid={`entity-files-delete-${f.id}`}>
                        <Trash className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (!data.children?.length && (
        <div className="py-8 text-center text-slate-500 text-sm border border-dashed border-slate-300 dark:border-slate-700 rounded-md" data-testid="entity-files-empty">
          No files in this folder yet. Use the Upload button to add documents or photos.
        </div>
      ))}

      {isAdmin && atRoot && (
        <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <Button size="sm" variant="outline" onClick={() => handleShare("folder", data.folder.id, data.folder.name)} data-testid="entity-files-share-folder-btn">
            <Share className="h-3.5 w-3.5 mr-1.5" /> Share whole folder
          </Button>
          <span className="text-[11px] text-slate-400">Folder: {data.folder.path}</span>
        </div>
      )}
    </div>
  );
}
